import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Button, Card, DatePicker, Select, Spin, Tag } from 'antd';
import {
    BulbOutlined,
    CheckOutlined,
    CloseOutlined,
    ClockCircleOutlined,
    FileTextOutlined,
    GlobalOutlined,
    ReloadOutlined,
    SwapOutlined,
    TruckOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import * as echarts from 'echarts';
import ReactEcharts from 'echarts-for-react';
import dayjs, { Dayjs } from 'dayjs';
import { IAppComponentProps } from '../../../../../components';
import { getI18n } from '../../../../../utils/Utils';
import { QUERY_EIM_EXECUTIVE_DASHBOARD_COUNTRY_METRICS } from '../../dashboardQueries';
import { EIM_THEME_STORAGE_KEY } from '../../../themePreference';
import { getRangePickerLocalization } from '../../../../info-portal/components/grid/CalendarUtils';
import worldGeoJson from '../../../../../assets/maps/world.json';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import { buildCompanyCountryLookup, getCountryDisplayName, getCountryFlagUrl, resolveCountryCode } from '../../eimDashboardUtils';
import styles from './countryPerformanceMonitoring.module.scss';

const { RangePicker } = DatePicker;

type ThemeMode = 'dark' | 'light';

type CountryMetric = {
    country?: string | null;
    companyCode?: string | null;
    intake?: {
        totalProcessed?: number | null;
        inboundCount?: number | null;
        outboundCount?: number | null;
    } | null;
    processing?: {
        stpRate?: number | null;
        onTimeRate?: number | null;
        avgTransitDays?: number | null;
        inTransitCount?: number | null;
    } | null;
    exceptions?: {
        failedCount?: number | null;
        overdueCount?: number | null;
        failureRate?: number | null;
    } | null;
    coverage?: {
        activePartners?: number | null;
        totalPartners?: number | null;
        coverageRate?: number | null;
    } | null;
};

type CountryMetricsQueryResult = {
    eimExecutiveDashboardCountryMetrics?: CountryMetric[] | null;
};

type CountryPerformanceCacheSnapshot = {
    countryMetrics: CountryMetric[];
};

type CountryPerformanceCacheEntry = {
    updatedAt: number;
    snapshot: CountryPerformanceCacheSnapshot;
};

type CountrySummary = {
    countryCode: string;
    name: string;
    totalProcessed: number;
    inboundCount: number;
    outboundCount: number;
    passedCount: number;
    overdueCount: number;
    failedCount: number;
    inTransitCount: number;
    activePartners: number;
    totalPartners: number;
    coverageRate: number;
    stpRate: number;
    onTimeRate: number;
    avgTransitDays: number;
    failureRate: number;
    healthTone: CountryHealthTone;
};

type CountryHealthTone = 'healthy' | 'warning' | 'critical' | 'noData';

type CountrySummaryAccumulator = {
    countryCode: string;
    name: string;
    totalProcessed: number;
    inboundCount: number;
    outboundCount: number;
    failedCount: number;
    overdueCount: number;
    inTransitCount: number;
    activePartners: number;
    totalPartners: number;
    weightedStpRateNumerator: number;
    weightedOnTimeRateNumerator: number;
    weightedTransitDaysNumerator: number;
};

type KpiDefinition = {
    key: string;
    labelId: string;
    defaultLabel: string;
    icon: React.ReactNode;
    tone: string;
    value: (summary: CountrySummary) => number;
};

type MapClickParams = {
    name?: string;
    data?: {
        countryCode?: string;
    };
};

const WORLD_MAP_NAME = 'bmw-country-performance-world';
const DEFAULT_RANGE_DAYS = 30;
const COUNTRY_PERFORMANCE_CACHE_PREFIX = 'countryPerformanceMonitoringCache:v1';
const COUNTRY_PERFORMANCE_CACHE_TTL_MS = 10 * 60 * 1000;
const toDateRange = (daysBack: number): [Dayjs, Dayjs] => {
    const end = dayjs();
    return [end.subtract(daysBack - 1, 'day').startOf('day'), end.endOf('day')];
};

const KPI_DEFINITIONS: KpiDefinition[] = [
    {
        key: 'total',
        labelId: 'bmw-einvoicing-country-kpi-total',
        defaultLabel: 'Total Invoices',
        icon: <FileTextOutlined />,
        tone: '#2563eb',
        value: (summary) => summary.totalProcessed,
    },
    {
        key: 'inbound',
        labelId: 'bmw-einvoicing-country-kpi-inbound',
        defaultLabel: 'I/B Invoices',
        icon: <SwapOutlined />,
        tone: '#7c3aed',
        value: (summary) => summary.inboundCount,
    },
    {
        key: 'outbound',
        labelId: 'bmw-einvoicing-country-kpi-outbound',
        defaultLabel: 'O/B Invoices',
        icon: <GlobalOutlined />,
        tone: '#15803d',
        value: (summary) => summary.outboundCount,
    },
    {
        key: 'passed',
        labelId: 'bmw-einvoicing-country-kpi-passed',
        defaultLabel: 'Passed',
        icon: <CheckOutlined />,
        tone: '#0f9d58',
        value: (summary) => summary.passedCount,
    },
    {
        key: 'overdue',
        labelId: 'bmw-einvoicing-country-kpi-overdue',
        defaultLabel: 'Overdue',
        icon: <ClockCircleOutlined />,
        tone: '#d97706',
        value: (summary) => summary.overdueCount,
    },
    {
        key: 'failed',
        labelId: 'bmw-einvoicing-country-kpi-failed',
        defaultLabel: 'Failed',
        icon: <CloseOutlined />,
        tone: '#dc2626',
        value: (summary) => summary.failedCount,
    },
    {
        key: 'transit',
        labelId: 'bmw-einvoicing-country-kpi-in-transit',
        defaultLabel: 'In Transit',
        icon: <TruckOutlined />,
        tone: '#1d4ed8',
        value: (summary) => summary.inTransitCount,
    },
];

const HEALTH_TONES: Record<CountryHealthTone, string> = {
    healthy: '#22c55e',
    warning: '#f59e0b',
    critical: '#ef4444',
    noData: '#94a3b8',
};

const getInitialTheme = (): ThemeMode => {
    try {
        const stored = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
    } catch {
        // Ignore storage access failures and fall back to the light page variant.
    }

    return 'light';
};

const formatCompactNumber = (value?: number | null): string => {
    const numericValue = Number(value ?? 0);
    if (Math.abs(numericValue) >= 1000000) {
        return `${(numericValue / 1000000).toFixed(2)}M`;
    }

    if (Math.abs(numericValue) >= 1000) {
        return `${(numericValue / 1000).toFixed(1)}k`;
    }

    return new Intl.NumberFormat().format(numericValue);
};

const getHealthTone = (summary: {
    totalProcessed: number;
    failureRate: number;
    inTransitCount: number;
    overdueCount: number;
}): CountryHealthTone => {
    if (summary.totalProcessed <= 0) {
        return 'noData';
    }

    if (summary.failureRate >= 5 || summary.overdueCount >= 5) {
        return 'critical';
    }

    const inTransitRate = (summary.inTransitCount * 100) / Math.max(1, summary.totalProcessed);
    if (inTransitRate >= 3 || summary.overdueCount > 0) {
        return 'warning';
    }

    return 'healthy';
};

const getBaseAccumulator = (countryCode: string, name: string): CountrySummaryAccumulator => ({
    countryCode,
    name,
    totalProcessed: 0,
    inboundCount: 0,
    outboundCount: 0,
    failedCount: 0,
    overdueCount: 0,
    inTransitCount: 0,
    activePartners: 0,
    totalPartners: 0,
    weightedStpRateNumerator: 0,
    weightedOnTimeRateNumerator: 0,
    weightedTransitDaysNumerator: 0,
});

const buildCountrySummary = (accumulator: CountrySummaryAccumulator): CountrySummary => {
    const totalProcessed = Math.max(0, accumulator.totalProcessed);
    const failedCount = Math.max(0, accumulator.failedCount);
    const overdueCount = Math.max(0, accumulator.overdueCount);
    const inTransitCount = Math.max(0, accumulator.inTransitCount);
    const passedCount = Math.max(0, totalProcessed - failedCount - overdueCount - inTransitCount);
    const activePartners = Math.max(0, accumulator.activePartners);
    const totalPartners = Math.max(0, accumulator.totalPartners);
    const coverageRate = totalPartners > 0 ? (activePartners * 100) / totalPartners : 0;
    const stpRate = totalProcessed > 0 ? accumulator.weightedStpRateNumerator / totalProcessed : 0;
    const onTimeRate = totalProcessed > 0 ? accumulator.weightedOnTimeRateNumerator / totalProcessed : 0;
    const avgTransitDays = totalProcessed > 0 ? accumulator.weightedTransitDaysNumerator / totalProcessed : 0;
    const failureRate = totalProcessed > 0 ? ((failedCount + overdueCount) * 100) / totalProcessed : 0;

    return {
        countryCode: accumulator.countryCode,
        name: accumulator.name,
        totalProcessed,
        inboundCount: Math.max(0, accumulator.inboundCount),
        outboundCount: Math.max(0, accumulator.outboundCount),
        passedCount,
        overdueCount,
        failedCount,
        inTransitCount,
        activePartners,
        totalPartners,
        coverageRate,
        stpRate,
        onTimeRate,
        avgTransitDays,
        failureRate,
        healthTone: getHealthTone({
            totalProcessed,
            failureRate,
            inTransitCount,
            overdueCount,
        }),
    };
};

const aggregateMetrics = (metrics: CountryMetric[], regionNames: Intl.DisplayNames | null, allCountriesLabel: string) => {
    const companyCountryLookup = buildCompanyCountryLookup(metrics);
    const groupedMetrics = new Map<string, CountrySummaryAccumulator>();

    metrics.forEach((metric) => {
        const countryCode = resolveCountryCode(metric, companyCountryLookup);
        if (countryCode.length !== 2) {
            return;
        }

        const countryName = getCountryDisplayName(countryCode, regionNames);
        const accumulator = groupedMetrics.get(countryCode) ?? getBaseAccumulator(countryCode, countryName);
        accumulator.name = countryName || accumulator.name;

        const totalProcessed = Math.max(0, Number(metric.intake?.totalProcessed ?? 0));
        const inboundCount = Math.max(0, Number(metric.intake?.inboundCount ?? 0));
        const outboundCount = Math.max(0, Number(metric.intake?.outboundCount ?? 0));
        const failedCount = Math.max(0, Number(metric.exceptions?.failedCount ?? 0));
        const overdueCount = Math.max(0, Number(metric.exceptions?.overdueCount ?? 0));
        const inTransitCount = Math.max(0, Number(metric.processing?.inTransitCount ?? 0));
        const activePartners = Math.max(0, Number(metric.coverage?.activePartners ?? 0));
        const totalPartners = Math.max(0, Number(metric.coverage?.totalPartners ?? 0));
        const stpRate = Math.max(0, Number(metric.processing?.stpRate ?? 0));
        const onTimeRate = Math.max(0, Number(metric.processing?.onTimeRate ?? 0));
        const avgTransitDays = Math.max(0, Number(metric.processing?.avgTransitDays ?? 0));

        accumulator.totalProcessed += totalProcessed;
        accumulator.inboundCount += inboundCount;
        accumulator.outboundCount += outboundCount;
        accumulator.failedCount += failedCount;
        accumulator.overdueCount += overdueCount;
        accumulator.inTransitCount += inTransitCount;
        accumulator.activePartners += activePartners;
        accumulator.totalPartners += totalPartners;
        accumulator.weightedStpRateNumerator += stpRate * totalProcessed;
        accumulator.weightedOnTimeRateNumerator += onTimeRate * totalProcessed;
        accumulator.weightedTransitDaysNumerator += avgTransitDays * totalProcessed;

        groupedMetrics.set(countryCode, accumulator);
    });

    const summaries = Array.from(groupedMetrics.values())
        .map((accumulator) => buildCountrySummary(accumulator))
        .sort((left, right) => right.totalProcessed - left.totalProcessed || left.name.localeCompare(right.name));

    const globalAccumulator = summaries.reduce<CountrySummaryAccumulator>((accumulator, summary) => {
        accumulator.totalProcessed += summary.totalProcessed;
        accumulator.inboundCount += summary.inboundCount;
        accumulator.outboundCount += summary.outboundCount;
        accumulator.failedCount += summary.failedCount;
        accumulator.overdueCount += summary.overdueCount;
        accumulator.inTransitCount += summary.inTransitCount;
        accumulator.activePartners += summary.activePartners;
        accumulator.totalPartners += summary.totalPartners;
        accumulator.weightedStpRateNumerator += summary.stpRate * summary.totalProcessed;
        accumulator.weightedOnTimeRateNumerator += summary.onTimeRate * summary.totalProcessed;
        accumulator.weightedTransitDaysNumerator += summary.avgTransitDays * summary.totalProcessed;
        return accumulator;
    }, getBaseAccumulator('ALL', allCountriesLabel));

    const globalSummary = buildCountrySummary(globalAccumulator);

    return {
        summaries,
        globalSummary,
    };
};

const getSummaryByCountry = (summaries: CountrySummary[], countryCode: string, globalSummary: CountrySummary) => {
    if (!countryCode) {
        return globalSummary;
    }

    return summaries.find((summary) => summary.countryCode === countryCode) ?? globalSummary;
};

const getSelectedCountryName = (countryCode: string, globalSummary: CountrySummary, selectedSummary: CountrySummary) => (
    countryCode ? selectedSummary.name : globalSummary.name
);

const getSummaryToneClass = (tone: CountryHealthTone): string => {
    if (tone === 'healthy') {
        return styles.healthHealthy;
    }

    if (tone === 'warning') {
        return styles.healthWarning;
    }

    if (tone === 'critical') {
        return styles.healthCritical;
    }

    return styles.healthNoData;
};

const escapeHtml = (value: string): string => value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

const getKpiToneClass = (tone: string): string => {
    if (tone === '#2563eb') {
        return styles.toneBlue;
    }

    if (tone === '#7c3aed') {
        return styles.tonePurple;
    }

    if (tone === '#15803d') {
        return styles.toneGreen;
    }

    if (tone === '#0f9d58') {
        return styles.toneEmerald;
    }

    if (tone === '#d97706') {
        return styles.toneAmber;
    }

    if (tone === '#dc2626') {
        return styles.toneRed;
    }

    return styles.toneBlue;
};

const getColorByHealthTone = (tone: CountryHealthTone): string => HEALTH_TONES[tone];

const CountryPerformanceMonitoring: FunctionComponent<IAppComponentProps> = (props) => {
    const { intl } = props;
    const [theme, setTheme] = useState<ThemeMode>(getInitialTheme);
    const [selectedRange, setSelectedRange] = useState<[Dayjs, Dayjs]>(() => toDateRange(DEFAULT_RANGE_DAYS));
    const [selectedCountryCode, setSelectedCountryCode] = useState<string>('');
    const [countryMetrics, setCountryMetrics] = useState<CountryMetric[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);

    const [getCountryMetrics] = useLazyQuery<CountryMetricsQueryResult>(QUERY_EIM_EXECUTIVE_DASHBOARD_COUNTRY_METRICS);

    const getCacheKey = useCallback((fromDate: Dayjs, toDate: Dayjs) => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        return `${COUNTRY_PERFORMANCE_CACHE_PREFIX}:${locale}:${fromDate.format('YYYY-MM-DD')}:${toDate.format('YYYY-MM-DD')}`;
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme((currentTheme) => {
            const nextTheme = currentTheme === 'dark' ? 'light' : 'dark';
            try {
                globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, nextTheme);
            } catch {
                // Ignore storage failures and keep the visual toggle working locally.
            }
            return nextTheme;
        });
    }, []);

    const loadDashboard = useCallback(async (forceRefresh = false) => {
        if (forceRefresh) {
            setLoadError(null);
        }

        setIsLoading(true);
        setLoadError(null);

        const [fromDate, toDate] = selectedRange;
        const cacheKey = getCacheKey(fromDate, toDate);

        if (forceRefresh) {
            globalThis.sessionStorage.removeItem(cacheKey);
        }

        if (!forceRefresh) {
            const cached = globalThis.sessionStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const parsed = JSON.parse(cached) as CountryPerformanceCacheEntry;
                    const isFresh = Number.isFinite(parsed?.updatedAt)
                        && (Date.now() - parsed.updatedAt) <= COUNTRY_PERFORMANCE_CACHE_TTL_MS;
                    if (isFresh) {
                        setCountryMetrics(parsed.snapshot?.countryMetrics ?? []);
                        setIsLoading(false);
                        return;
                    }
                    globalThis.sessionStorage.removeItem(cacheKey);
                } catch {
                    globalThis.sessionStorage.removeItem(cacheKey);
                }
            }
        }

        try {
            const response = await getCountryMetrics({
                variables: {
                    request: {
                        periodStart: fromDate.toISOString(),
                        periodEnd: toDate.toISOString(),
                    },
                },
                fetchPolicy: 'network-only',
            });

            const countrySnapshot = response?.data?.eimExecutiveDashboardCountryMetrics ?? [];
            setCountryMetrics(countrySnapshot);
            const cacheEntry: CountryPerformanceCacheEntry = {
                updatedAt: Date.now(),
                snapshot: {
                    countryMetrics: countrySnapshot,
                },
            };
            globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(cacheEntry));
        } catch (error) {
            console.error('Failed to load BMW country dashboard data.', error);
            setCountryMetrics([]);
            setLoadError(getI18n(
                'bmw-einvoicing-country-dashboard-load-error',
                'Unable to load the country dashboard. Please try again.',
                intl
            ));
        } finally {
            setIsLoading(false);
        }
    }, [getCacheKey, getCountryMetrics, intl, selectedRange]);

    useEffect(() => {
        loadDashboard().catch(() => {
            // loadDashboard already updates the error state.
        });
    }, [loadDashboard]);

    useEffect(() => {
        try {
            echarts.registerMap(WORLD_MAP_NAME, worldGeoJson as never);
        } catch {
            // Ignore duplicate registration during hot reload.
        }
    }, []);

    const regionNames = useMemo(() => {
        if (!Intl.DisplayNames) {
            return null;
        }

        return new Intl.DisplayNames(['en'], { type: 'region' });
    }, []);

    const allCountriesLabel = getI18n('country-performance-monitoring-all-countries', 'All countries', intl);

    const { summaries, globalSummary } = useMemo(
        () => aggregateMetrics(countryMetrics, regionNames, allCountriesLabel),
        [allCountriesLabel, countryMetrics, regionNames]
    );

    const selectedSummary = useMemo(
        () => getSummaryByCountry(summaries, selectedCountryCode, globalSummary),
        [globalSummary, selectedCountryCode, summaries]
    );

    const selectedCountryLabel = useMemo(
        () => (selectedCountryCode ? selectedSummary.name : getI18n('bmw-einvoicing-country-dashboard-all-countries', 'All countries', intl)),
        [intl, selectedCountryCode, selectedSummary.name]
    );

    const countryOptions = useMemo(() => [
        {
            label: getI18n('bmw-einvoicing-country-dashboard-all-countries', 'All countries', intl),
            value: '',
        },
        ...summaries.map((summary) => ({
            label: summary.name,
            value: summary.countryCode,
        })),
    ], [intl, summaries]);

    const topCountryRows = useMemo(() => summaries.slice(0, 8), [summaries]);

    const countrySummaryLookup = useMemo(() => new Map(summaries.map((summary) => [summary.countryCode, summary])), [summaries]);

    const mapSeriesData = useMemo(() => summaries.map((summary) => ({
        name: summary.name,
        value: summary.totalProcessed,
        countryCode: summary.countryCode,
        itemStyle: {
            areaColor: selectedCountryCode === summary.countryCode
                ? '#2563eb'
                : getColorByHealthTone(summary.healthTone),
            borderColor: selectedCountryCode === summary.countryCode ? '#ffffff' : 'rgba(255, 255, 255, 0.72)',
            borderWidth: selectedCountryCode === summary.countryCode ? 2 : 1,
        },
    })), [selectedCountryCode, summaries]);

    const mapOption = useMemo(() => ({
        animation: true,
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            backgroundColor: theme === 'light' ? 'rgba(255, 255, 255, 0.98)' : 'rgba(15, 23, 42, 0.96)',
            borderColor: theme === 'light' ? 'rgba(148, 163, 184, 0.35)' : 'rgba(148, 163, 184, 0.2)',
            textStyle: {
                color: theme === 'light' ? '#0f172a' : '#f8fafc',
            },
            formatter: (params: { name?: string; data?: { countryCode?: string } }) => {
                const countryCode = String(params.data?.countryCode ?? '').trim().toUpperCase();
                const country = countrySummaryLookup.get(countryCode);
                if (!country) {
                    return escapeHtml(params.name ?? '');
                }

                const statusLabel = getI18n(
                    `eim-world-map-health-${country.healthTone}`,
                    country.healthTone === 'healthy' ? 'Healthy' : country.healthTone === 'warning' ? 'Warning' : country.healthTone === 'critical' ? 'Critical' : 'No data',
                    intl
                );
                const countryFlagSrc = getCountryFlagUrl(country.countryCode);
                const flagMarkup = countryFlagSrc
                    ? `<img src="${countryFlagSrc}" alt="${escapeHtml(country.name)}" width="20" height="20" style="display:block;height:20px;object-fit:cover;width:20px;" onerror="this.style.display='none';" />`
                    : '';

                return `
                    <div style="align-items:center;display:flex;gap:8px;font-weight:700;margin-bottom:6px;">
                        <span style="align-items:center;background:#fff;border:1px solid rgba(148,163,184,0.35);border-radius:999px;display:inline-flex;height:20px;justify-content:center;overflow:hidden;width:20px;">${flagMarkup}</span>
                        <span>${escapeHtml(country.name)}</span>
                    </div>
                    <div>${escapeHtml(getI18n('bmw-einvoicing-country-dashboard-map-tooltip-total', 'Total invoices', intl))}: ${formatCompactNumber(country.totalProcessed)}</div>
                    <div>${escapeHtml(getI18n('bmw-einvoicing-country-dashboard-map-tooltip-failures', 'Failed + overdue', intl))}: ${formatCompactNumber(country.failedCount + country.overdueCount)}</div>
                    <div>${escapeHtml(getI18n('bmw-einvoicing-country-dashboard-map-tooltip-in-transit', 'In transit', intl))}: ${formatCompactNumber(country.inTransitCount)}</div>
                    <div>${escapeHtml(getI18n('bmw-einvoicing-country-dashboard-map-tooltip-status', 'Health', intl))}: ${escapeHtml(statusLabel)}</div>
                `;
            },
        },
        series: [
            {
                type: 'map',
                map: WORLD_MAP_NAME,
                roam: true,
                roamTrigger: 'global',
                scaleLimit: {
                    min: 0.9,
                    max: 8,
                },
                selectedMode: false,
                label: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: false,
                    },
                    itemStyle: {
                        areaColor: '#93c5fd',
                        borderColor: '#ffffff',
                        borderWidth: 2,
                    },
                },
                data: mapSeriesData,
            },
        ],
    }), [countrySummaryLookup, intl, mapSeriesData, theme]);

    const mapEvents = useMemo(() => ({
        click: (params: MapClickParams) => {
            const clickedCountryCode = String(params.data?.countryCode ?? '').trim().toUpperCase();
            if (clickedCountryCode) {
                setSelectedCountryCode(clickedCountryCode);
            }
        },
    }), []);

    const dashboardCards = useMemo(() => KPI_DEFINITIONS.map((definition) => ({
        ...definition,
        value: definition.value(selectedSummary),
    })), [selectedSummary]);

    const selectedHealthLabel = getI18n(
        `eim-world-map-health-${selectedSummary.healthTone}`,
        selectedSummary.healthTone === 'healthy' ? 'Healthy' : selectedSummary.healthTone === 'warning' ? 'Warning' : selectedSummary.healthTone === 'critical' ? 'Critical' : 'No data',
        intl
    );

    const selectedHealthToneClass = getSummaryToneClass(selectedSummary.healthTone);

    return (
        <Spin spinning={isLoading} size="large">
            <div className={classNames(
                styles.page,
                theme === 'light' ? styles.pageLight : styles.pageDark
            )}
            >
                <div className={styles.shell}>
                    <header className={styles.header}>
                        <div>
                            <div className={styles.eyebrow}>
                                {getI18n('bmw-einvoicing-country-dashboard-eyebrow', 'BMW Country View', intl)}
                            </div>
                            <h1 className={styles.title}>
                                {getI18n('bmw-einvoicing-country-dashboard-title', 'BMW Country Performance Dashboard', intl)}
                            </h1>
                            <p className={styles.subtitle}>
                                {getI18n(
                                    'bmw-einvoicing-country-dashboard-subtitle',
                                    'Select a country on the map to filter the KPI cards and summary details.',
                                    intl
                                )}
                            </p>
                        </div>

                        <div className={styles.headerControls}>
                            <div className={styles.controlRow}>
                                <span className={styles.controlLabel}>
                                    {getI18n('cfo-reporting-period', 'Period', intl)}
                                </span>
                                <RangePicker
                                    className={styles.periodPicker}
                                    value={selectedRange}
                                    onChange={(values) => {
                                        if (values?.[0] && values?.[1]) {
                                            setSelectedRange([values[0], values[1]]);
                                        }
                                    }}
                                    allowClear={false}
                                    format="MMM D, YYYY"
                                    locale={getRangePickerLocalization(intl, globalThis.localStorage.getItem('locale') || 'en')}
                                />
                            </div>
                            <div className={styles.controlRow}>
                                <Select
                                    className={styles.countrySelect}
                                    options={countryOptions}
                                    value={selectedCountryCode}
                                    onChange={(value) => setSelectedCountryCode(String(value ?? ''))}
                                    showSearch={true}
                                    allowClear={true}
                                    placeholder={getI18n('bmw-einvoicing-country-dashboard-country-select', 'Select a country', intl)}
                                    optionFilterProp="label"
                                    aria-label={getI18n('bmw-einvoicing-country-dashboard-country-select', 'Select a country', intl)}
                                />
                                <Tag className={styles.selectionTag} color={selectedCountryCode ? 'blue' : 'default'}>
                                    {selectedCountryCode ? (
                                        <CountryFlagLabel countryCode={selectedCountryCode} label={selectedCountryLabel} />
                                    ) : selectedCountryLabel}
                                </Tag>
                            </div>

                            <div className={styles.controlRow}>
                                <Button icon={<BulbOutlined />} onClick={toggleTheme} className={styles.toolbarButton}>
                                    {theme === 'dark'
                                        ? getI18n('cfo-reporting-switch-light', 'Switch to light view', intl)
                                        : getI18n('cfo-reporting-switch-dark', 'Switch to dark view', intl)}
                                </Button>
                                <Button icon={<ReloadOutlined />} onClick={() => loadDashboard(true)} className={styles.toolbarButton}>
                                    {getI18n('cfo-reporting-refresh', 'Refresh', intl)}
                                </Button>
                            </div>
                        </div>
                    </header>

                    {loadError ? (
                        <Card className={styles.errorCard} bordered={false}>
                            <div className={styles.errorTitle}>
                                {getI18n('bmw-einvoicing-country-dashboard-load-error-title', 'Dashboard data is unavailable', intl)}
                            </div>
                            <div className={styles.errorMessage}>{loadError}</div>
                        </Card>
                    ) : null}

                    <section className={styles.kpiGrid} aria-label={getI18n('bmw-einvoicing-country-dashboard-kpi-grid', 'Key metrics', intl)}>
                        {dashboardCards.map((card) => (
                            <Card key={card.key} className={styles.kpiCard} bordered={false}>
                                <div className={styles.kpiTopRow}>
                                    <div
                                        className={classNames(styles.kpiIcon, getKpiToneClass(card.tone))}
                                        style={{
                                            color: card.tone,
                                            backgroundColor: `${card.tone}18`,
                                        }}
                                    >
                                        {card.icon}
                                    </div>
                                    <div className={styles.kpiLabel}>
                                        {getI18n(card.labelId, card.defaultLabel, intl)}
                                    </div>
                                </div>
                                <div className={styles.kpiValue}>{formatCompactNumber(card.value)}</div>
                                <div className={styles.kpiFoot}>
                                    {selectedCountryCode
                                        ? selectedCountryLabel
                                        : getI18n('bmw-einvoicing-country-dashboard-global-context', 'Global view', intl)}
                                </div>
                            </Card>
                        ))}
                    </section>

                    <section className={styles.mainGrid}>
                        <Card className={styles.mapCard} bordered={false}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h2>{getI18n('bmw-einvoicing-country-dashboard-map-title', 'World view', intl)}</h2>
                                    <p>{getI18n('bmw-einvoicing-country-dashboard-map-hint', 'Click a country to filter the KPI cards below.', intl)}</p>
                                </div>
                                <Tag className={classNames(styles.healthTag, selectedHealthToneClass)}>
                                    {selectedHealthLabel}
                                </Tag>
                            </div>

                            <div className={styles.mapStage}>
                                <div className={styles.mapCanvas}>
                                    <ReactEcharts
                                        option={mapOption}
                                        onEvents={mapEvents}
                                        style={{ height: '100%', width: '100%' }}
                                    />
                                </div>

                                <aside className={styles.summaryPane}>
                                    <div className={styles.summaryPaneHeader}>
                                        <div>
                                            <div className={styles.summaryPaneEyebrow}>
                                                {getI18n('bmw-einvoicing-country-dashboard-summary-eyebrow', 'Selected country', intl)}
                                            </div>
                                            <h3>
                                                {selectedCountryCode ? (
                                                    <CountryFlagLabel
                                                        countryCode={selectedCountryCode}
                                                        label={getSelectedCountryName(selectedCountryCode, globalSummary, selectedSummary)}
                                                    />
                                                ) : getSelectedCountryName(selectedCountryCode, globalSummary, selectedSummary)}
                                            </h3>
                                        </div>
                                        <Tag color={selectedCountryCode ? 'blue' : 'default'}>
                                            {selectedCountryLabel}
                                        </Tag>
                                    </div>

                                    <div className={styles.summaryStats}>
                                        <div className={styles.summaryStatRow}>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-total', 'Total invoices', intl)}</span>
                                            <strong>{formatCompactNumber(selectedSummary.totalProcessed)}</strong>
                                        </div>
                                        <div className={styles.summaryStatRow}>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-passed', 'Passed', intl)}</span>
                                            <strong>{formatCompactNumber(selectedSummary.passedCount)}</strong>
                                        </div>
                                        <div className={styles.summaryStatRow}>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-failed', 'Failed', intl)}</span>
                                            <strong>{formatCompactNumber(selectedSummary.failedCount)}</strong>
                                        </div>
                                        <div className={styles.summaryStatRow}>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-overdue', 'Overdue', intl)}</span>
                                            <strong>{formatCompactNumber(selectedSummary.overdueCount)}</strong>
                                        </div>
                                        <div className={styles.summaryStatRow}>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-transit', 'In transit', intl)}</span>
                                            <strong>{formatCompactNumber(selectedSummary.inTransitCount)}</strong>
                                        </div>
                                        <div className={styles.summaryStatRow}>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-coverage', 'Coverage', intl)}</span>
                                            <strong>{`${intl.formatNumber(selectedSummary.coverageRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}</strong>
                                        </div>
                                    </div>

                                    <div className={styles.summaryFooter}>
                                        <div>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-health', 'Health', intl)}</span>
                                            <strong>{selectedHealthLabel}</strong>
                                        </div>
                                        <div>
                                            <span>{getI18n('bmw-einvoicing-country-dashboard-summary-transit-days', 'Avg. transit', intl)}</span>
                                            <strong>{`${intl.formatNumber(selectedSummary.avgTransitDays, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}d`}</strong>
                                        </div>
                                    </div>
                                </aside>
                            </div>
                        </Card>

                        <Card className={styles.listCard} bordered={false}>
                            <div className={styles.sectionHeader}>
                                <div>
                                    <h2>{getI18n('bmw-einvoicing-country-dashboard-top-countries-title', 'Top countries', intl)}</h2>
                                    <p>{getI18n('bmw-einvoicing-country-dashboard-top-countries-hint', 'Use the list to switch countries quickly.', intl)}</p>
                                </div>
                            </div>

                            <ul className={styles.countryList}>
                                {topCountryRows.map((country) => {
                                    const sharePercent = globalSummary.totalProcessed > 0
                                        ? (country.totalProcessed * 100) / globalSummary.totalProcessed
                                        : 0;

                                    return (
                                        <li key={country.countryCode}>
                                            <button
                                                type="button"
                                                className={classNames(
                                                    styles.countryRowButton,
                                                    selectedCountryCode === country.countryCode ? styles.countryRowSelected : ''
                                                )}
                                                onClick={() => setSelectedCountryCode(country.countryCode)}
                                            >
                                                <CountryFlagLabel
                                                    countryCode={country.countryCode}
                                                    label={country.name}
                                                    className={styles.countryName}
                                                />
                                                <span className={styles.countryValue}>{formatCompactNumber(country.totalProcessed)}</span>
                                                <span className={styles.countryBar}>
                                                    <span
                                                        className={styles.countryBarFill}
                                                        style={{ width: `${Math.max(4, Math.round(sharePercent))}%` }}
                                                    />
                                                </span>
                                                <span className={styles.countryShare}>
                                                    {`${intl.formatNumber(sharePercent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}
                                                </span>
                                            </button>
                                        </li>
                                    );
                                })}

                                {topCountryRows.length === 0 ? (
                                    <li className={styles.emptyState}>
                                        {getI18n('bmw-einvoicing-country-dashboard-empty', 'No country data available.', intl)}
                                    </li>
                                ) : null}
                            </ul>
                        </Card>
                    </section>
                </div>
            </div>
        </Spin>
    );
};

export default CountryPerformanceMonitoring;
